import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { HiOutlineFilter } from "react-icons/hi";
import Search from "components/common/Search/Search";
import SelectOption from "components/common/Select/Select";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_SP_CRM_FORM_TEMPLATES,
  URL_SP_FORM_CATEGGORES,
} from "store/services/URL";
import {
  PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_IDS,
  PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_NAMES,
  PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_SLUGS,
} from "Constants/constant";
import { parseBooleanVal } from "Utils/utils";
import DocumentRow from "./DocumentRow";

const normalizeFormName = (name) => (name || "").trim().toLowerCase();

const normalizeCategoryName = (name) => normalizeFormName(name);

const getFormCategoryId = (form) =>
  form?.form_category_id ??
  form?.form_category?.id ??
  form?.formCategory?.id ??
  null;

const getFormCategorySlug = (form) =>
  (
    form?.form_category?.slug ??
    form?.formCategory?.slug ??
    form?.category_slug ??
    ""
  ).toLowerCase();

const getFormCategoryLabel = (form) =>
  form?.category_name ||
  form?.form_category?.name ||
  form?.formCategory?.name ||
  "";

export const isExcludedParticipantComplianceCategory = (category) => {
  if (!category) return false;

  const categoryId = Number(category.id ?? category.value);
  if (
    Number.isFinite(categoryId) &&
    PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_IDS.includes(categoryId)
  ) {
    return true;
  }

  const slug = (category.slug || "").toLowerCase();
  if (
    slug &&
    PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_SLUGS.includes(slug)
  ) {
    return true;
  }

  const name = normalizeCategoryName(category.name ?? category.label);
  return PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_NAMES.includes(name);
};

export const isExcludedParticipantComplianceForm = (form) => {
  if (!form) return false;

  const categoryId = Number(getFormCategoryId(form));
  if (
    Number.isFinite(categoryId) &&
    PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_IDS.includes(categoryId)
  ) {
    return true;
  }

  const slug = getFormCategorySlug(form);
  if (
    slug &&
    PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_SLUGS.includes(slug)
  ) {
    return true;
  }

  const categoryName = normalizeCategoryName(getFormCategoryLabel(form));
  return PARTICIPANT_COMPLIANCE_EXCLUDED_FORM_CATEGORY_NAMES.includes(
    categoryName
  );
};

const getFormKey = (form) =>
  form?.sp_form_template_id != null
    ? `template:${Number(form.sp_form_template_id)}`
    : `name:${normalizeFormName(form?.name)}`;

const sortByStableOrder = (forms, stableOrderKeys) => {
  if (!Array.isArray(stableOrderKeys) || stableOrderKeys.length === 0) {
    return forms;
  }
  const orderMap = new Map(stableOrderKeys.map((key, index) => [key, index]));
  const fallbackRank = stableOrderKeys.length;
  return [...forms].sort((a, b) => {
    const aIdx = orderMap.get(getFormKey(a));
    const bIdx = orderMap.get(getFormKey(b));
    const aOrder = aIdx !== undefined ? aIdx : fallbackRank;
    const bOrder = bIdx !== undefined ? bIdx : fallbackRank;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.name || "").localeCompare(b.name || "");
  });
};

const sortComplianceFirst = (forms) =>
  [...forms].sort((a, b) => {
    const aOn = parseBooleanVal(a.is_compliance) ? 1 : 0;
    const bOn = parseBooleanVal(b.is_compliance) ? 1 : 0;
    if (bOn !== aOn) return bOn - aOn;
    return (a.name || "").localeCompare(b.name || "");
  });

const findComplianceForm = (complianceForms, templateId) =>
  (complianceForms || []).find(
    (f) => Number(f.sp_form_template_id) === Number(templateId)
  );

const extractFormTemplates = (res) => {
  const payload = res?.data?.data ?? res?.data ?? res ?? {};
  const templates = payload?.form_templates;
  if (Array.isArray(templates)) return templates;
  if (Array.isArray(templates?.data)) return templates.data;
  return [];
};

const mergeCatalogForm = (crmForm, complianceForms) => {
  const existing = findComplianceForm(complianceForms, crmForm.id);
  const categoryName =
    crmForm.category_name ||
    crmForm.form_category?.name ||
    crmForm.formCategory?.name ||
    null;
  if (existing) {
    return {
      ...existing,
      category_name: categoryName || existing.category_name || null,
    };
  }
  return {
    sp_form_template_id: crmForm.id,
    name: crmForm.title,
    is_compliance: false,
    score: 0,
    type: "form",
    category_name: categoryName,
  };
};

const formMatchesSearch = (form, searchTerm) => {
  const query = normalizeFormName(searchTerm);
  if (!query) return true;
  return (
    normalizeFormName(form.name).includes(query) ||
    normalizeFormName(form.category_name).includes(query)
  );
};

export const buildDisplayForms = (
  catalogForms,
  complianceForms,
  hasActiveFilter = false,
  searchTerm = "",
  preserveCatalogOrder = false,
  stableOrderKeys = null
) => {
  const visibleCatalogForms = (catalogForms || []).filter(
    (form) => !isExcludedParticipantComplianceForm(form)
  );
  const visibleComplianceForms = (complianceForms || []).filter(
    (form) => !isExcludedParticipantComplianceForm(form)
  );

  const merged = visibleCatalogForms.map((crmForm) =>
    mergeCatalogForm(crmForm, visibleComplianceForms)
  );

  if (hasActiveFilter) {
    const filtered = searchTerm.trim()
      ? merged.filter((form) => formMatchesSearch(form, searchTerm))
      : merged;

    let result;
    if (searchTerm.trim()) {
      const filteredKeys = new Set(filtered.map(getFormKey));
      const matchingExtras = visibleComplianceForms
        .filter(
          (form) =>
            form.sp_form_template_id != null &&
            !filteredKeys.has(getFormKey(form)) &&
            formMatchesSearch(form, searchTerm)
        )
        .map((form) => ({
          ...form,
          type: "form",
        }));
      result = [...filtered, ...matchingExtras];
    } else {
      result = filtered;
    }

    if (preserveCatalogOrder && stableOrderKeys) {
      return sortByStableOrder(result, stableOrderKeys);
    }

    return sortComplianceFirst(result);
  }

  const mergedKeys = new Set(merged.map(getFormKey));

  const extras = visibleComplianceForms
    .filter(
      (form) =>
        form.sp_form_template_id != null && !mergedKeys.has(getFormKey(form))
    )
    .map((form) => ({
      ...form,
      type: "form",
    }));

  const ordered = [...merged, ...extras];
  if (preserveCatalogOrder) {
    return sortByStableOrder(ordered, stableOrderKeys);
  }
  return sortComplianceFirst(ordered);
};

export { getFormKey };

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-800 px-2.5 py-1 text-xs font-medium">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-full p-0.5 hover:bg-indigo-200/80 transition-colors"
      aria-label={`Remove ${label} filter`}
    >
      <FiX className="w-3 h-3" />
    </button>
  </span>
);

export const ParticipantFormFilters = ({
  search,
  setSearch,
  selectedCategories,
  setSelectedCategories,
  categoryOptions = [],
  catalogLoading = false,
  hasActiveFilter = false,
  resultCount = null,
  onClear,
}) => {
  const removeCategory = (categoryId) => {
    setSelectedCategories(
      (selectedCategories || []).filter(
        (c) => (c.value ?? c.id) !== categoryId
      )
    );
  };

  return (
    <div className="px-5 py-4 bg-white border-b border-indigo-100 shrink-0">
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <span className="flex-shrink-0 mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
              <HiOutlineFilter className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                Browse CRM forms
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                All CRM forms are shown by default. Search or filter by category
                to narrow the list. When filtering, mandatory forms appear at the
                top initially. Toggling mandatory status keeps each form in place
                until you click Update Changes.
              </p>
            </div>
          </div>
          {hasActiveFilter && onClear && (
            <button
              type="button"
              onClick={onClear}
              className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <FiX className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="participant-form-search"
              className="block text-xs font-medium text-gray-600 mb-1.5"
            >
              Search forms
            </label>
            <div className="relative">
              <Search
                search={search}
                setSearch={setSearch}
                placeholder="e.g. Initial Incident Injury Form"
                customClassSearch="w-full h-10 border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                width="w-full"
              />
              {catalogLoading && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <span
                    className="inline-block h-4 w-4 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin"
                    aria-hidden="true"
                  />
                </span>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Categories
            </label>
            <SelectOption
              isMulti
              isSearch
              isClearable
              placeHolder="All categories"
              data={categoryOptions}
              selectedOption={selectedCategories}
              handleChange={(next) => setSelectedCategories(next ?? [])}
              cssClass="z-30"
              isDisabled={catalogLoading}
            />
          </div>
        </div>

        {hasActiveFilter && (
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <span className="text-xs font-medium text-gray-500">Active:</span>
            {search?.trim() && (
              <FilterChip
                label={`"${search.trim()}"`}
                onRemove={() => setSearch("")}
              />
            )}
            {(selectedCategories || []).map((cat) => (
              <FilterChip
                key={cat.value ?? cat.id}
                label={cat.label}
                onRemove={() => removeCategory(cat.value ?? cat.id)}
              />
            ))}
            {catalogLoading ? (
              <span className="text-xs text-gray-400 ml-auto">Searching…</span>
            ) : resultCount != null ? (
              <span className="text-xs text-gray-500 ml-auto tabular-nums">
                {resultCount} {resultCount === 1 ? "form" : "forms"} found
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export const useParticipantFormCatalog = ({
  search,
  selectedCategories,
}) => {
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [catalogForms, setCatalogForms] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const hasActiveFilter =
    debouncedSearch.trim().length > 0 || selectedCategories.length > 0;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await apiGetRequest(URL_SP_FORM_CATEGGORES);
        const categories =
          res?.data?.data?.form_categories ||
          res?.data?.form_categories ||
          res?.data?.data ||
          [];
        setCategoryOptions(
          (Array.isArray(categories) ? categories : [])
            .filter((cat) => !isExcludedParticipantComplianceCategory(cat))
            .map((cat) => ({
              id: cat.id,
              value: cat.id,
              label: cat.name,
            }))
        );
      } catch (error) {
        console.error("Failed to load CRM form categories:", error);
      }
    };
    loadCategories();
  }, []);

  const fetchCatalogForms = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("status", "publish");
      params.append("per_page", "500");
      params.append("page", "1");
      if (debouncedSearch.trim()) {
        params.append("search", debouncedSearch.trim());
      }

      const filter = {
        status: "publish",
        is_hidden: false,
      };
      if (selectedCategories.length > 0) {
        filter.form_category = selectedCategories.map(
          (c) => c.value ?? c.id
        );
      }

      const res = await apiPostRequest(
        `${URL_SP_CRM_FORM_TEMPLATES}?${params.toString()}`,
        { filter }
      );
      setCatalogForms(
        extractFormTemplates(res).filter(
          (form) => !isExcludedParticipantComplianceForm(form)
        )
      );
    } catch (error) {
      console.error("Failed to load CRM forms:", error);
      setCatalogForms([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [debouncedSearch, selectedCategories]);

  useEffect(() => {
    fetchCatalogForms();
  }, [fetchCatalogForms]);

  return {
    categoryOptions,
    catalogForms,
    catalogLoading,
    hasActiveFilter,
    debouncedSearch,
  };
};

const ParticipantFormsSection = ({
  complianceForms = [],
  lastErrorLocation,
  onSwitchChange,
  onScoreChange,
  onLastErrorChange,
  mandatorySubtitle = "Mandatory for all participants",
  catalogForms = [],
  catalogLoading = false,
  hasActiveFilter = false,
  searchTerm = "",
  preserveCatalogOrder = false,
  stableOrderKeys = null,
}) => {
  const displayForms = useMemo(
    () =>
      buildDisplayForms(
        catalogForms,
        complianceForms,
        hasActiveFilter,
        searchTerm,
        preserveCatalogOrder,
        stableOrderKeys
      ),
    [
      catalogForms,
      complianceForms,
      hasActiveFilter,
      searchTerm,
      preserveCatalogOrder,
      stableOrderKeys,
    ]
  );

  const mandatoryCount = displayForms.filter((f) =>
    parseBooleanVal(f.is_compliance)
  ).length;

  return (
    <div className="bg-white overflow-hidden">
      {displayForms.length > 0 && (
        <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50 border-b border-gray-100">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {hasActiveFilter ? "Filtered forms" : "All forms"}
          </span>
          <span className="text-xs text-gray-500 tabular-nums">
            {mandatoryCount} mandatory · {displayForms.length} shown
          </span>
        </div>
      )}
      <div className="divide-y divide-gray-100 bg-white px-1 min-h-[80px]">
        {catalogLoading && displayForms.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10">
            <CircularLoader width_size="w-16" />
            <p className="text-sm text-gray-500">Loading CRM forms…</p>
          </div>
        ) : displayForms.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-5 py-10 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <FiSearch className="w-5 h-5" />
            </span>
            <p className="text-sm font-medium text-gray-700">
              {hasActiveFilter ? "No forms found" : "No forms available"}
            </p>
            <p className="text-xs text-gray-500 max-w-sm">
              {hasActiveFilter
                ? "Try a different search term or category, or clear filters to show all forms."
                : "No published CRM forms were found for this provider."}
            </p>
          </div>
        ) : (
          displayForms.map((item) => (
            <DocumentRow
              key={getFormKey(item)}
              item={item}
              complianceType="form"
              lastErrorLocation={lastErrorLocation}
              onSwitchChange={onSwitchChange}
              onScoreChange={onScoreChange}
              onLastErrorChange={onLastErrorChange}
              icon="form"
              mandatorySubtitle={mandatorySubtitle}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ParticipantFormsSection;
